/**
 * Homepage coordination for OGT rebuild sections.
 * - Offer builder (#s-offer / #ogt-offer-data)
 * - Cart add from offer CTA (variant + optional selling_plan → cart drawer)
 * - Carousel arrows (.ogt-car-nav)
 * - Deep links (.ogt-sub-link / .ogt-ot-link → plan + scroll; data-ogt-range)
 *
 * No-ops when required DOM is missing. Pricing comes only from
 * <script type="application/json" id="ogt-offer-data"> (no prototype fallback).
 */
(function () {
  'use strict';

  window.OGT = window.OGT || {};

  var S = (window.OGT.state = {
    plan: 'sub',
    range: 'o2',
    qty: 0,
    flavour: 0
  });
  var DATA = null;
  var submitting = false;

  function loadThemeModule(name) {
    if (typeof window.__ogtLoadThemeModule === 'function') {
      return window.__ogtLoadThemeModule(name);
    }
    return Promise.resolve();
  }

  function waitFor(predicate, tries) {
    tries = tries == null ? 40 : tries;
    return new Promise(function (resolve) {
      (function tick(left) {
        if (predicate() || left <= 0) return resolve();
        requestAnimationFrame(function () {
          tick(left - 1);
        });
      })(tries);
    });
  }

  function ensureCartModules() {
    var chain = Promise.resolve();
    if (!customElements.get('site-drawers')) {
      chain = chain.then(function () {
        return loadThemeModule('drawers');
      });
    }
    if (!customElements.get('shopify-cart')) {
      chain = chain.then(function () {
        return loadThemeModule('shopify-cart');
      });
    }
    return chain
      .then(function () {
        return customElements.whenDefined('site-drawers');
      })
      .then(function () {
        return customElements.whenDefined('shopify-cart');
      })
      .then(function () {
        return waitFor(function () {
          return !!(window.drawers && document.querySelector('shopify-cart'));
        });
      });
  }

  function refreshCartDrawer() {
    var cart = document.querySelector('shopify-cart');
    if (
      !cart ||
      typeof cart.render !== 'function' ||
      typeof cart.getSectionsToRender !== 'function'
    ) {
      return Promise.resolve(false);
    }

    var sections = cart.getSectionsToRender().map(function (section) {
      return section.section;
    });
    var sectionUrl = window.location.pathname + '?sections=' + sections.join(',');
    cart.loading = true;

    var statePromise =
      typeof cart.getCart === 'function' ? cart.getCart() : Promise.resolve(null);

    return statePromise
      .then(function (state) {
        if (state) cart.cartState = state;
        return fetch(sectionUrl, {
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        }).then(function (res) {
          return res.json();
        });
      })
      .then(function (renderedSections) {
        return cart.render({ sections: renderedSections });
      })
      .then(function () {
        return true;
      })
      .catch(function () {
        cart.loading = false;
        return false;
      });
  }

  function openCartSidebar() {
    return ensureCartModules()
      .then(function () {
        if (window.drawers) {
          window.drawers.activeDrawer = 'cart';
        } else {
          var trigger = document.querySelector('[data-drawer-trigger="cart"]');
          if (!trigger) return false;
          trigger.click();
        }
        return refreshCartDrawer().then(function () {
          return true;
        });
      })
      .catch(function () {
        return false;
      });
  }

  window.OGT.openCartSidebar = openCartSidebar;

  function readOfferData() {
    var el = document.getElementById('ogt-offer-data');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function moneyAmount(v) {
    return (Math.round((Number(v) + Number.EPSILON) * 100) / 100).toFixed(2);
  }

  function money(v) {
    return '£' + moneyAmount(v);
  }

  function per(price, bars) {
    return '£' + (Number(price) / bars).toFixed(2);
  }

  function q() {
    return DATA[S.range].qty[S.qty];
  }

  function freqKey() {
    var freq = document.getElementById('freq');
    if (!freq) return '4';
    return String(freq.value || '4').indexOf('2') === 0 ? '2' : '4';
  }

  function resolveSellingPlanId(tier, range) {
    var plans = (tier && tier.plans) || (range && range.plans) || {};
    return plans[freqKey()] || plans['4'] || plans['2'] || (range && range.sellingPlanId) || null;
  }

  /* ---- chip press state (any [data-group] / .ogt-chip) ---- */
  function initChipGroups(root) {
    (root || document).querySelectorAll('[data-group]').forEach(function (g) {
      if (g.__ogtBound) return;
      g.__ogtBound = true;
      g.addEventListener('click', function (e) {
        var b = e.target.closest('.ogt-chip');
        if (!b) return;
        g.querySelectorAll('.ogt-chip').forEach(function (c) {
          c.setAttribute('aria-pressed', 'false');
        });
        b.setAttribute('aria-pressed', 'true');
      });
    });
  }

  /* ---- carousel arrows ---- */
  function initCarousels(root) {
    (root || document).querySelectorAll('.ogt-car-nav').forEach(function (nav) {
      if (nav.__ogtBound) return;
      var car = document.getElementById(nav.dataset.for);
      if (!car) return;
      nav.__ogtBound = true;
      nav.addEventListener('click', function (e) {
        var b = e.target.closest('.ogt-car-btn');
        if (!b) return;
        var slide = car.querySelector('.ogt-slide');
        if (!slide) return;
        var cs = getComputedStyle(car);
        var gap = parseFloat(cs.columnGap || cs.gap) || 16;
        car.scrollBy({
          left: (slide.getBoundingClientRect().width + gap) * (+b.dataset.dir),
          behavior: 'smooth'
        });
      });
    });
  }

  function flavourMeta(f) {
    if (!f) return { name: '', tray: 'variety', image: '' };
    if (typeof f === 'string') {
      var parts = f.split('|');
      return {
        name: parts[0] || '',
        tray: parts[1] || 'variety',
        image: parts[2] || ''
      };
    }
    return {
      name: f.name || '',
      tray: f.tray || 'variety',
      image: f.image || ''
    };
  }

  function setGalleryImage(src, className) {
    var gi = document.getElementById('ogalImg');
    if (!gi || !src) return;
    var current = gi.getAttribute('src') || '';
    if (className) gi.className = className;
    if (current === src || current.indexOf(src) !== -1) {
      gi.style.opacity = 1;
      return;
    }
    gi.style.opacity = 0;
    setTimeout(function () {
      gi.src = src;
      gi.style.opacity = 1;
    }, 180);
  }

  /* ---- offer builder ---- */
  function paintTrays(tray) {
    document.querySelectorAll('.ogt-oconf .ogt-minitray').forEach(function (mt) {
      if (tray === 'variety') {
        mt.classList.add('variety');
        mt.style.removeProperty('--tray');
      } else {
        mt.classList.remove('variety');
        mt.style.setProperty('--tray', tray);
      }
    });
  }

  function updateSaveNote() {
    var n = document.getElementById('saveNote');
    var freq = document.getElementById('freq');
    if (!n || !freq || !DATA || !DATA[S.range]) return;
    var d = DATA[S.range];
    var base = d.qty[0];
    var k = q();
    var packs = k.bars / 12;
    var introSave = packs * (base.ot - base.sub);
    var onSave = packs * (base.ot - base.subOn);
    var wk = freq.value.indexOf('2') === 0 ? 2 : 4;
    var boxes = Math.floor(52 / wk);
    var yr = introSave * 3 + (boxes - 3) * onSave;
    n.textContent =
      'Save £' +
      moneyAmount(introSave) +
      ' on each of your first three boxes, then £' +
      moneyAmount(onSave) +
      ' every box after — around £' +
      moneyAmount(yr) +
      ' in your first year.';
  }

  function benefitLines(raw) {
    return String(raw || '')
      .split(/\r?\n/)
      .map(function (l) {
        return l.trim();
      })
      .filter(Boolean);
  }

  function renderPlanBenefitLists() {
    if (!DATA || !DATA[S.range]) return;
    var d = DATA[S.range];
    var iconTpl = document.getElementById('ogtPlanTickIcon');
    var subList = document.getElementById('subBenefitsList');
    var otList = document.getElementById('otBenefitsList');

    if (subList) {
      var subLines = benefitLines(d.subBenefits);
      var frag = document.createDocumentFragment();
      var hasOngoing = false;
      subLines.forEach(function (st) {
        var li = document.createElement('li');
        if (iconTpl) li.appendChild(iconTpl.content.cloneNode(true));
        if (st.indexOf('{ongoing}') !== -1) {
          hasOngoing = true;
          var span = document.createElement('span');
          span.id = 'ongoing';
          span.textContent = d.ongoing || '';
          li.appendChild(span);
        } else {
          li.appendChild(document.createTextNode(st));
        }
        frag.appendChild(li);
      });
      if (!hasOngoing) {
        var liOn = document.createElement('li');
        if (iconTpl) liOn.appendChild(iconTpl.content.cloneNode(true));
        var spanOn = document.createElement('span');
        spanOn.id = 'ongoing';
        spanOn.textContent = d.ongoing || '';
        liOn.appendChild(spanOn);
        frag.appendChild(liOn);
      }
      subList.replaceChildren(frag);
    }

    if (otList) {
      var otLines = benefitLines(d.otBenefits);
      var otFrag = document.createDocumentFragment();
      otLines.forEach(function (ot) {
        var li = document.createElement('li');
        var x = document.createElement('span');
        x.className = 'ogt-xm';
        x.setAttribute('aria-hidden', 'true');
        x.textContent = '✕';
        li.appendChild(x);
        li.appendChild(document.createTextNode(ot));
        otFrag.appendChild(li);
      });
      otList.replaceChildren(otFrag);
      otList.hidden = otLines.length === 0;
    }
  }

  function renderGalleryPanel() {
    if (!DATA || !DATA[S.range]) return;
    var d = DATA[S.range];
    var gt = document.getElementById('ogalTag');
    var ge = document.getElementById('ogalEdu');
    var ticks = document.getElementById('ogalTicks');
    var iconTpl = document.getElementById('ogtGalTickIcon');
    var gi = document.getElementById('ogalImg');

    if (gt && typeof d.galTag === 'string') gt.textContent = d.galTag;
    if (ge) {
      var h = ge.querySelector('h4');
      var p = ge.querySelector('p');
      if (h && typeof d.galHeading === 'string') h.textContent = d.galHeading;
      if (p && typeof d.galBody === 'string') p.textContent = d.galBody;
    }
    if (gi && d.galCls) gi.className = d.galCls;

    if (ticks) {
      var lines = benefitLines(d.galTicks);
      var frag = document.createDocumentFragment();
      lines.forEach(function (t) {
        var li = document.createElement('li');
        if (iconTpl) li.appendChild(iconTpl.content.cloneNode(true));
        li.appendChild(document.createTextNode(t));
        frag.appendChild(li);
      });
      ticks.replaceChildren(frag);
      ticks.hidden = lines.length === 0;
    }
  }

  function refreshOffer() {
    var offer = document.getElementById('s-offer');
    if (!offer || !DATA || !DATA[S.range]) return;
    var d = DATA[S.range];
    var k = q();

    var subPrice = document.getElementById('subPrice');
    var subWas = document.getElementById('subWas');
    var subUnit = document.getElementById('subUnit');
    var otPrice = document.getElementById('otPrice');
    var otUnit = document.getElementById('otUnit');
    if (subPrice) subPrice.textContent = money(k.sub);
    if (subWas) subWas.textContent = money(k.ot);
    if (subUnit) subUnit.textContent = per(k.sub, k.bars) + ' per bar';
    if (otPrice) otPrice.textContent = money(k.ot);
    if (otUnit) otUnit.textContent = per(k.ot, k.bars) + ' per bar';
    renderPlanBenefitLists();
    renderGalleryPanel();

    var cta = offer.querySelector('.ogt-offerb__cta');
    if (cta && !cta.disabled) {
      cta.textContent =
        S.plan === 'sub'
          ? 'Start my subscription — ' + money(k.sub)
          : 'Add to basket — ' + money(k.ot);
    }

    if (S.flavour >= d.flavours.length) S.flavour = 0;
    var fg = offer.querySelector('[data-flavours]');
    if (fg) {
      fg.innerHTML = d.flavours
        .map(function (f, i) {
          var meta = flavourMeta(f);
          return (
            '<button type="button" class="ogt-chip" aria-pressed="' +
            (i === S.flavour) +
            '" data-tray="' +
            meta.tray +
            '"' +
            (meta.image ? ' data-gal-img="' + meta.image + '"' : '') +
            '>' +
            meta.name +
            '</button>'
          );
        })
        .join('');
    }

    var oconf = offer.querySelector('.ogt-oconf');
    if (oconf) {
      oconf.className = oconf.className
        .split(/\s+/)
        .filter(function (c) {
          return c && c.indexOf('range-') !== 0;
        })
        .join(' ');
      if (S.range) oconf.classList.add('range-' + S.range);
    }

    var chips = offer.querySelectorAll(
      '[data-group]:not([data-ranges]):not([data-flavours]) .ogt-chip'
    );
    d.qty.forEach(function (tk, i) {
      var chip = chips[i];
      if (!chip) return;
      var price = S.plan === 'sub' ? tk.sub : tk.ot;
      var save = S.plan === 'sub' ? 0 : (tk.bars / 12) * d.qty[0].ot - tk.ot;
      var lbl =
        (i === 0 ? '12 bars' : String(tk.bars)) +
        ' — ' +
        money(price).replace('.00', '') +
        (save > 0.005 ? ' · save £' + moneyAmount(save) : '');
      var trays = tk.bars / 12;
      var tray = '';
      for (var t = 0; t < trays; t++) {
        tray +=
          '<span class="ogt-minitray variety" aria-hidden="true">' +
          new Array(13).join('<i></i>') +
          '</span>';
      }
      chip.innerHTML = tray + lbl;
      chip.setAttribute('aria-pressed', String(i === S.qty));
    });

    // After qty chips are rebuilt — keep tray + gallery on the selected flavour
    var curFlavour = flavourMeta(d.flavours[S.flavour]);
    paintTrays(curFlavour.tray);
    if (curFlavour.image) setGalleryImage(curFlavour.image);
    else if (d.galImage) setGalleryImage(d.galImage);

    var tl = offer.querySelector('.ogt-trustlines--c');
    if (tl) tl.style.display = S.plan === 'sub' ? '' : 'none';

    updateSaveNote();
  }

  function setCtaBusy(cta, busy, label) {
    if (!cta) return;
    submitting = busy;
    cta.disabled = !!busy;
    if (label) cta.textContent = label;
  }

  function addOfferToCart() {
    var cta = document.getElementById('ogtOfferCta');
    if (!cta || submitting || !DATA || !DATA[S.range]) return;

    var range = DATA[S.range];
    var tier = q();
    var variantId = tier.variantId || range.variantId;
    if (!variantId) {
      setCtaBusy(cta, false, 'Unavailable — pick a product in the theme editor');
      return;
    }

    var quantity = tier.quantity || Math.max(1, Math.round(tier.bars / 12));
    var payload = {
      items: [
        {
          id: Number(variantId),
          quantity: Number(quantity)
        }
      ]
    };

    if (S.plan === 'sub') {
      var planId = resolveSellingPlanId(tier, range);
      if (!planId) {
        setCtaBusy(cta, false, 'Subscription unavailable — try one-time');
        return;
      }
      payload.items[0].selling_plan = Number(planId);
    }

    var addUrl = cta.getAttribute('data-cart-add') || (window.routes && window.routes.cart_add_url) || '/cart/add';
    var cartUrl = cta.getAttribute('data-cart-url') || (window.routes && window.routes.cart_url) || '/cart';
    var idleLabel =
      S.plan === 'sub'
        ? 'Start my subscription — ' + money(tier.sub)
        : 'Add to basket — ' + money(tier.ot);

    setCtaBusy(cta, true, 'Adding…');

    fetch(addUrl + '.js', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(payload)
    })
      .then(function (res) {
        if (!res.ok) {
          return res.json().then(function (err) {
            throw err;
          });
        }
        return openCartSidebar().then(function (opened) {
          setCtaBusy(cta, false, idleLabel);
          refreshOffer();
          if (!opened && cartUrl) {
            window.location.href = cartUrl;
          }
        });
      })
      .catch(function () {
        setCtaBusy(cta, false, idleLabel);
        cta.textContent = 'Couldn’t add — try again';
        setTimeout(function () {
          if (!submitting) refreshOffer();
        }, 2200);
      });
  }

  function pickRange(rangeKey) {
    if (!rangeKey || !DATA || !DATA[rangeKey]) return;
    S.range = rangeKey;
    S.flavour = 0;
    var chip = document.querySelector('[data-ranges] .ogt-chip[data-range="' + rangeKey + '"]');
    if (chip) {
      var group = chip.closest('[data-group]');
      if (group) {
        group.querySelectorAll('.ogt-chip').forEach(function (c) {
          c.setAttribute('aria-pressed', String(c === chip));
        });
      }
      chip.click();
    } else {
      refreshOffer();
    }
  }

  function initOffer() {
    var offer = document.getElementById('s-offer');
    if (!offer || !DATA) return;

    var planGroup = document.getElementById('planGroup');
    if (planGroup && !planGroup.__ogtBound) {
      planGroup.__ogtBound = true;
      planGroup.addEventListener('click', function (e) {
        if (e.target.closest('.ogt-freqrow')) return;
        var b = e.target.closest('.ogt-plan');
        if (!b) return;
        S.plan = b.dataset.plan;
        planGroup.querySelectorAll('.ogt-plan').forEach(function (p) {
          p.setAttribute('aria-checked', String(p === b));
        });
        refreshOffer();
      });
      planGroup.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
          var other = planGroup.querySelector('.ogt-plan[aria-checked="false"]');
          if (other) {
            other.click();
            other.focus();
          }
          e.preventDefault();
        }
        if ((e.key === 'Enter' || e.key === ' ') && !e.target.closest('.ogt-freqrow')) {
          var pl = e.target.closest('.ogt-plan');
          if (pl) pl.click();
          e.preventDefault();
        }
      });
    }

    var freq = document.getElementById('freq');
    if (freq && !freq.__ogtBound) {
      freq.__ogtBound = true;
      freq.addEventListener('change', updateSaveNote);
    }

    var ranges = offer.querySelector('[data-ranges]');
    if (ranges && !ranges.__ogtRangeBound) {
      ranges.__ogtRangeBound = true;
      ranges.addEventListener('click', function (e) {
        var b = e.target.closest('.ogt-chip');
        if (!b) return;
        S.range = b.dataset.range;
        S.flavour = 0;
        refreshOffer();
      });
    }

    offer.querySelectorAll('[data-flavours]').forEach(function (g) {
      if (g.__ogtFlavBound) return;
      g.__ogtFlavBound = true;
      g.addEventListener('click', function (e) {
        var b = e.target.closest('.ogt-chip');
        if (!b || !b.dataset.tray) return;
        if (g.closest('.ogt-oconf')) {
          S.flavour = Array.prototype.indexOf.call(g.children, b);
        }
        paintTrays(b.dataset.tray);
        if (b.dataset.galImg) setGalleryImage(b.dataset.galImg);
      });
    });

    offer.querySelectorAll('[data-group]').forEach(function (g) {
      if (g.hasAttribute('data-ranges') || g.hasAttribute('data-flavours') || g.__ogtQtyBound) {
        return;
      }
      g.__ogtQtyBound = true;
      g.addEventListener('click', function (e) {
        var b = e.target.closest('.ogt-chip');
        if (!b) return;
        S.qty = Array.prototype.indexOf.call(g.children, b);
        var m = document.getElementById('traymult');
        if (m) m.textContent = S.qty === 1 ? '×2' : S.qty === 2 ? '×3' : '';
        refreshOffer();
      });
    });

    var cta = document.getElementById('ogtOfferCta');
    if (cta && !cta.__ogtBound) {
      cta.__ogtBound = true;
      cta.addEventListener('click', function (e) {
        e.preventDefault();
        addOfferToCart();
      });
    }

    if (!DATA[S.range]) {
      S.range = Object.keys(DATA)[0] || S.range;
    }

    refreshOffer();
  }

  /* ---- deep links → pick plan + smooth-scroll ---- */
  function smoothScrollTo(hash) {
    if (!hash || hash.charAt(0) !== '#' || hash.length < 2) return false;
    var id = hash.slice(1).split('?')[0];
    var target = document.getElementById(id);
    if (!target) return false;
    var reduced =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
    return true;
  }

  function initDeepLinks(root) {
    var pickPlan = function (sel) {
      var el = document.querySelector(sel);
      if (el && el.getAttribute('aria-checked') !== 'true') el.click();
    };
    var bind = function (selector, planSel) {
      (root || document).querySelectorAll(selector).forEach(function (a) {
        if (a.__ogtBound) return;
        a.__ogtBound = true;
        a.addEventListener('click', function (e) {
          if (a.getAttribute('data-ogt-range')) pickRange(a.getAttribute('data-ogt-range'));
          if (planSel) pickPlan(planSel);
          var href = a.getAttribute('href');
          if (href && smoothScrollTo(href)) e.preventDefault();
        });
      });
    };
    bind('.ogt-ot-link', '.ogt-plan--ot');
    bind('.ogt-sub-link', '.ogt-plan--sub');
    /* Range-only deep links (e.g. goal tiles with plan = none) */
    bind('a[data-ogt-range]:not(.ogt-sub-link):not(.ogt-ot-link)', null);
  }

  /* ---- FAQ accordion height animation ---- */
  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function faqPanelOf(details) {
    return details.querySelector('.ogt-faq__panel');
  }

  function faqSetHeight(panel, px) {
    panel.style.height = typeof px === 'number' ? px + 'px' : px;
  }

  function faqAnimateClose(details, done) {
    var panel = faqPanelOf(details);
    if (!panel) {
      details.open = false;
      if (done) done();
      return;
    }
    if (details.__ogtFaqAnimating) return;
    details.__ogtFaqAnimating = true;
    details.classList.add('is-closing');

    var start = panel.scrollHeight;
    faqSetHeight(panel, start);
    panel.offsetHeight;

    var finished = false;
    var finish = function () {
      if (finished) return;
      finished = true;
      details.__ogtFaqAnimating = false;
      details.open = false;
      details.classList.remove('is-closing');
      faqSetHeight(panel, 0);
      panel.removeEventListener('transitionend', onEnd);
      if (done) done();
    };
    var onEnd = function (e) {
      if (e && e.target !== panel) return;
      finish();
    };

    if (prefersReducedMotion()) {
      finish();
      return;
    }
    requestAnimationFrame(function () {
      faqSetHeight(panel, 0);
    });
    panel.addEventListener('transitionend', onEnd);
    setTimeout(finish, 400);
  }

  function faqAnimateOpen(details) {
    var panel = faqPanelOf(details);
    if (!panel) {
      details.open = true;
      return;
    }
    if (details.__ogtFaqAnimating) return;
    details.__ogtFaqAnimating = true;

    details.open = true;
    faqSetHeight(panel, 0);
    panel.offsetHeight;
    var end = panel.scrollHeight;

    var finished = false;
    var finish = function () {
      if (finished) return;
      finished = true;
      details.__ogtFaqAnimating = false;
      faqSetHeight(panel, 'auto');
      panel.removeEventListener('transitionend', onEnd);
    };
    var onEnd = function (e) {
      if (e && e.target !== panel) return;
      finish();
    };

    if (prefersReducedMotion()) {
      faqSetHeight(panel, 'auto');
      details.__ogtFaqAnimating = false;
      return;
    }
    requestAnimationFrame(function () {
      faqSetHeight(panel, end);
    });
    panel.addEventListener('transitionend', onEnd);
    setTimeout(finish, 400);
  }

  function initFaqAccordions(root) {
    (root || document).querySelectorAll('.ogt-faq__list').forEach(function (list) {
      if (list.__ogtFaqBound) return;
      list.__ogtFaqBound = true;
      list.classList.add('ogt-faq__list--animated');

      list.querySelectorAll('details.ogt-faq__item').forEach(function (details) {
        var panel = faqPanelOf(details);
        var summary = details.querySelector('summary');
        if (!panel || !summary) return;

        if (details.open) {
          faqSetHeight(panel, 'auto');
        } else {
          faqSetHeight(panel, 0);
        }

        summary.addEventListener('click', function (e) {
          e.preventDefault();
          if (details.__ogtFaqAnimating) return;

          if (details.open) {
            faqAnimateClose(details);
            return;
          }

          var name = details.getAttribute('name');
          if (name) {
            var sel = 'details.ogt-faq__item[open]';
            list.querySelectorAll(sel).forEach(function (other) {
              if (other !== details && other.getAttribute('name') === name) {
                faqAnimateClose(other);
              }
            });
          }

          faqAnimateOpen(details);
        });
      });
    });
  }

  /* ---- boot ---- */
  function initAll(root) {
    DATA = readOfferData();
    initChipGroups(root);
    initCarousels(root);
    initOffer();
    initDeepLinks(root);
    initFaqAccordions(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
})();
